import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Download, ShieldAlert } from "lucide-react";
import Seo from "@/components/Seo";
import ArticleBody from "@/components/ArticleBody";
import { Container, Section } from "@/components/layout/Section";
import { api } from "@/lib/api";
import { ROUTE_SEO } from "@/config/seoData";

const LEGAL_SEO = {
  "privacy-policy": ROUTE_SEO.privacy,
  "terms-and-conditions": ROUTE_SEO.terms,
  "refund-and-cancellation-policy": ROUTE_SEO.refunds,
  "non-disclosure-agreement": ROUTE_SEO.nda,
};

export const LEGAL_DOCS = [
  { key: "privacy-policy", path: "/privacy-policy", label: "Privacy Policy" },
  { key: "terms-and-conditions", path: "/terms-and-conditions", label: "Terms & Conditions" },
  { key: "refund-and-cancellation-policy", path: "/refund-and-cancellation-policy", label: "Refunds & Cancellations" },
  { key: "non-disclosure-agreement", path: "/non-disclosure-agreement", label: "Mutual NDA" },
];

const Legal = ({ docKey }) => {
  const [doc, setDoc] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    api.get(`/legal/${docKey}`).then((r) => { setDoc(r.data); setState("ok"); }).catch(() => setState("error"));
  }, [docKey]);

  if (state === "loading") {
    return <Section className="pt-40"><Container><p className="text-sm text-brand-mist">Loading…</p></Container></Section>;
  }
  if (state === "error") {
    return (
      <Section className="flex min-h-[50vh] items-center pt-32" testid="legal-not-found">
        <Container className="space-y-4">
          <h1 className="font-display text-3xl font-bold text-brand-snow">Document not found</h1>
          <Link to="/" className="text-sm font-medium text-brand-emerald">Back to home</Link>
        </Container>
      </Section>
    );
  }

  return (
    <>
      <Seo
        title={LEGAL_SEO[docKey]?.title || doc.title}
        description={LEGAL_SEO[docKey]?.description || `${doc.title} for A2Z Rankings.`}
        canonicalPath={LEGAL_SEO[docKey]?.canonicalPath}
        ogImage={LEGAL_SEO[docKey]?.ogImage}
        jsonLd={LEGAL_SEO[docKey]?.jsonLd || []}
      />
      <Section testid={`legal-page-${docKey}`} className="pt-32 sm:pt-40">
        <Container className="max-w-3xl space-y-8">
          <div className="space-y-4">
            <p className="eyebrow">Legal</p>
            <h1 className="font-display text-3xl font-bold tracking-tight text-brand-snow sm:text-4xl">{doc.title}</h1>
            {doc.updated_at && (
              <p className="text-xs text-brand-mist">Last updated: {new Date(doc.updated_at).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}</p>
            )}
          </div>
          {!doc.reviewed && (
            <p className="flex items-start gap-3 rounded-xl border border-brand-azure/30 bg-brand-azure/10 px-5 py-4 text-sm leading-relaxed text-brand-azure" data-testid="legal-review-banner">
              <ShieldAlert size={17} className="mt-0.5 shrink-0" />
              This document is a carefully prepared draft pending qualified legal review. It must be reviewed by a
              legal professional before reliance, and does not claim independent compliance certification.
            </p>
          )}
          {docKey === "non-disclosure-agreement" && (
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/legal/non-disclosure-agreement.pdf`}
              data-testid="nda-download-pdf"
              className="inline-flex items-center gap-2 rounded-full bg-brand-emerald px-6 py-3 font-display text-sm font-semibold text-brand-black transition-colors hover:bg-brand-snow"
            >
              <Download size={15} /> Download PDF
            </a>
          )}
          <ArticleBody content={doc.content} testid="legal-content" />
          <div className="flex flex-wrap gap-4 border-t border-brand-mist/10 pt-8" data-testid="legal-nav">
            {LEGAL_DOCS.filter((d) => d.key !== docKey).map((d) => (
              <Link key={d.key} to={d.path} data-testid={`legal-link-${d.key}`} className="text-sm font-medium text-brand-mist transition-colors hover:text-brand-emerald">
                {d.label}
              </Link>
            ))}
          </div>
        </Container>
      </Section>
    </>
  );
};

export default Legal;
